import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GraduationCap, Plus, Users, Clock, CheckCircle, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminTraining() {
  const { token } = useAuth();
  const [courses, setCourses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAssign, setShowAssign] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', content_type: 'document', content_url: '', duration_minutes: '', required: false });
  const [assignDriver, setAssignDriver] = useState('');
  const [assignDue, setAssignDue] = useState('');
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkCourse, setBulkCourse] = useState('');
  const [autoAssign, setAutoAssign] = useState({});

  const fetchData = () => {
    Promise.all([
      api.get('/training/courses'),
      api.get('/users?role=driver'),
    ]).then(([cRes, dRes]) => {
      setCourses(cRes.data);
      setDrivers(dRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [token]);

  const handleCreate = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    try {
      await api.post('/training/courses', {
        ...form,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : undefined,
      });
      toast.success('Course created');
      setShowCreate(false);
      setForm({ title: '', description: '', content_type: 'document', content_url: '', duration_minutes: '', required: false });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAssign = async () => {
    if (!assignDriver || !showAssign) return;
    try {
      await api.post('/training/assignments', {
        course_id: showAssign.course_id,
        user_id: assignDriver,
        due_date: assignDue || undefined,
      });
      toast.success('Course assigned');
      setShowAssign(null);
      setAssignDriver('');
      setAssignDue('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleBulkAssign = async (courseId) => {
    try {
      let assigned = 0;
      for (const d of drivers) {
        try {
          await api.post('/training/assignments', { course_id: courseId, user_id: d.user_id });
          assigned++;
        } catch {}
      }
      toast.success(`Assigned to ${assigned} drivers`);
    } catch (err) { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-training">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">Training Management</h1>
        <div className="flex gap-2">
          <Button className="bg-muted text-foreground border border-input hover:bg-muted/80 text-xs font-bold" onClick={() => setShowBulkAssign(true)}>
            <Users className="w-4 h-4 mr-2" /> BULK ASSIGN
          </Button>
          <Button className="bg-brand-accent text-brand-accent-foreground hover:bg-yellow-500" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-2" /> Create Course
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-surface border-border"><CardContent className="pt-4 text-center">
          <p className="text-3xl font-bold text-foreground">{courses.length}</p><p className="text-xs text-muted-foreground mt-1">Courses</p>
        </CardContent></Card>
        <Card className="bg-surface border-border"><CardContent className="pt-4 text-center">
          <p className="text-3xl font-bold text-blue-400">{drivers.length}</p><p className="text-xs text-muted-foreground mt-1">Drivers</p>
        </CardContent></Card>
        <Card className="bg-surface border-border"><CardContent className="pt-4 text-center">
          <p className="text-3xl font-bold text-emerald-400">{courses.filter(c => c.required).length}</p><p className="text-xs text-muted-foreground mt-1">Required Courses</p>
        </CardContent></Card>
      </div>

      {/* Course List */}
      <div className="space-y-3">
        {courses.map(c => (
          <Card key={c.course_id} className="bg-surface border-border">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-accent/20 flex items-center justify-center shrink-0">
                    <BookOpen className="w-5 h-5 text-foreground" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-foreground font-medium">{c.title}</p>
                      {c.required && <Badge className="bg-red-500/20 text-red-400 text-[10px]">Required</Badge>}
                      <Badge className="bg-zinc-700 text-muted-foreground text-[10px] capitalize">{c.content_type}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{c.description || 'No description'}</p>
                    {c.duration_minutes && <p className="text-xs text-muted-foreground mt-1"><Clock className="w-3 h-3 inline mr-1" />{c.duration_minutes} min</p>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="border-input text-muted-foreground text-xs" onClick={() => setShowAssign(c)}>
                      <Users className="w-3 h-3 mr-1" /> Assign
                    </Button>
                    <Button size="sm" variant="outline" className="border-input text-muted-foreground text-xs" onClick={() => handleBulkAssign(c.course_id)}>
                      Assign All
                    </Button>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-[10px] text-muted-foreground">Auto-assign new drivers</span>
                    <input type="checkbox" checked={!!autoAssign[c.course_id]}
                      onChange={e => {
                        setAutoAssign(prev => ({ ...prev, [c.course_id]: e.target.checked }));
                        toast.success(e.target.checked ? `Auto-assign enabled for ${c.title}` : `Auto-assign disabled for ${c.title}`);
                      }}
                      className="w-4 h-4 rounded bg-muted border-input text-brand-accent cursor-pointer" />
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {courses.length === 0 && <p className="text-center text-muted-foreground py-12">No courses created yet</p>}
      </div>

      {/* Create Course Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-surface border-border text-foreground">
          <DialogHeader><DialogTitle>Create Training Course</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Course title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="bg-muted border-input text-foreground" />
            <Input placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="bg-muted border-input text-foreground" />
            <div className="flex gap-3">
              <Select value={form.content_type} onValueChange={v => setForm({ ...form, content_type: v })}>
                <SelectTrigger className="bg-muted border-input text-foreground"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-muted border-input">
                  <SelectItem value="document" className="text-foreground">Document</SelectItem>
                  <SelectItem value="video" className="text-foreground">Video</SelectItem>
                  <SelectItem value="quiz" className="text-foreground">Quiz</SelectItem>
                  <SelectItem value="link" className="text-foreground">Link</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Duration (min)" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })}
                className="bg-muted border-input text-foreground w-32" type="number" />
            </div>
            <Input placeholder="Content URL" value={form.content_url} onChange={e => setForm({ ...form, content_url: e.target.value })}
              className="bg-muted border-input text-foreground" />
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={form.required} onChange={e => setForm({ ...form, required: e.target.checked })} className="rounded" />
              Required for all drivers
            </label>
            <Button className="w-full bg-brand-accent text-brand-accent-foreground" onClick={handleCreate}>Create Course</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={!!showAssign} onOpenChange={() => setShowAssign(null)}>
        <DialogContent className="bg-surface border-border text-foreground">
          <DialogHeader><DialogTitle>Assign: {showAssign?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={assignDriver} onValueChange={setAssignDriver}>
              <SelectTrigger className="bg-muted border-input text-foreground"><SelectValue placeholder="Select driver" /></SelectTrigger>
              <SelectContent className="bg-muted border-input">
                {drivers.map(d => <SelectItem key={d.user_id} value={d.user_id} className="text-foreground">{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={assignDue} onChange={e => setAssignDue(e.target.value)}
              className="bg-muted border-input text-foreground" placeholder="Due date (optional)" />
            <Button className="w-full bg-brand-accent text-brand-accent-foreground" onClick={handleAssign} disabled={!assignDriver}>Assign Course</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={showBulkAssign} onOpenChange={setShowBulkAssign}>
        <DialogContent className="bg-surface border-border text-foreground">
          <DialogHeader><DialogTitle>Bulk Assign Course to All Drivers</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={bulkCourse} onValueChange={setBulkCourse}>
              <SelectTrigger className="bg-muted border-input text-foreground"><SelectValue placeholder="Select course" /></SelectTrigger>
              <SelectContent className="bg-muted border-input">
                {courses.map(c => <SelectItem key={c.course_id} value={c.course_id} className="text-foreground">{c.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">This will assign the selected course to all {drivers.length} drivers.</p>
            <Button className="w-full bg-brand-accent text-brand-accent-foreground" disabled={!bulkCourse}
              onClick={() => { handleBulkAssign(bulkCourse); setShowBulkAssign(false); setBulkCourse(''); }}>
              Assign to All Drivers
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
